import { getBuyingFormatSequence } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  BidStatus,
  OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import type { OrderResponseDto } from '@b-stock/order-api-client'

import type { AuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import { useIsMobile } from '@/buyer-portal/hooks/layout'

import {
  Body,
  BuyNowDescription,
  BuyNowHeader,
  ns,
  StyledInteractiveIcon,
  useActionStates,
} from '../../Bid/BuyNowActions/BuyNowActions'
import { BuyNowStatus } from '../../Bid/Tag'
import { ActionButton } from '../ui'

type BuyNowActionsProps = {
  editDeliveryMethod: () => void
  quickBuy: () => void
  buyNowStatus?: BidStatus.NO_BID | BuyNowStatus
  order?: OrderResponseDto | null
  auctionDetailState: AuctionDetailState
  listing: OrchestratedAuctionListingDetails
  shippingQuotePending?: boolean
  /** The selected quote is a parcel zero-quote, so the lot can't be bought. */
  hasNoParcelRate?: boolean
}

export const BuyNowActions = ({
  editDeliveryMethod,
  quickBuy,
  buyNowStatus,
  order,
  auctionDetailState,
  listing,
  shippingQuotePending = false,
  hasNoParcelRate = false,
}: BuyNowActionsProps) => {
  const { auction } = listing
  const mobileScreen = useIsMobile()

  const {
    primaryLabel,
    handlePrimaryBtnOnClick,
    displayBuyNowDesc,
    showPrimaryButton,
    primaryButtonAppearance,
  } = useActionStates({
    editDeliveryMethod,
    order,
    buyNowStatus,
    auctionDetailState,
    listing,
    quickBuy,
    shippingQuotePending,
  })

  if (!showPrimaryButton) return null

  const isLoading = primaryButtonAppearance === 'loading'
  const buyNowOnly = getBuyingFormatSequence(auction) === 'BINO'

  return (
    <Body>
      {auction.closed ? null : (
        <BuyNowHeader>
          <div>
            <FormattedMessage id={`${ns}.header`} />
            <StyledInteractiveIcon tooltipProps={{ placement: 'bottom' }}>
              <FormattedMessage
                id={`${ns}.tooltip.${buyNowOnly ? 'buyNowOnly' : 'buyNow'}`}
              />
            </StyledInteractiveIcon>
          </div>
        </BuyNowHeader>
      )}
      <ActionButton
        type="button"
        appearance={shippingQuotePending ? 'loading' : primaryButtonAppearance}
        disabled={
          shippingQuotePending ||
          isLoading ||
          (hasNoParcelRate && buyNowStatus !== BuyNowStatus.BOUGHT)
        }
        size={mobileScreen ? 'medium' : 'large'}
        onClick={handlePrimaryBtnOnClick}
      >
        {primaryLabel}
      </ActionButton>
      {displayBuyNowDesc ? (
        <BuyNowDescription>
          <FormattedMessage
            id={`${ns}.${buyNowOnly ? 'descriptionBuyNowOnly' : 'description'}`}
          />
        </BuyNowDescription>
      ) : null}
    </Body>
  )
}
